import { Box, Button, IconButton, Divider, FormControl, InputLabel, Stack, Paper, MenuItem, Typography, Grid, ToggleButton, ToggleButtonGroup, Select, SelectChangeEvent } from '@mui/material';

import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SlideComponentHkcsQ3CharacterOptionModel } from '@/types/slide';

import { Popover } from '@/components/utils/Popover/Popover';

import { Pronunciation } from '../components/Pronunciation';

import { WordPicker } from '@/components/inputs/WordPicker/WordPicker';
import { PhraseHighlight } from '@/components/displays/PhraseHighlight';

import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';

const SIZE = {
    width: 440,
    height: 640,
};

type WordPickerProps = {
    characterOptions?: SlideComponentHkcsQ3CharacterOptionModel[];
};

interface Props {
    anchorEl: HTMLElement | null;
    value: WordPickerProps;
    onClose: () => void;
    onUpdate: (settings: WordPickerProps) => void;
    slotProps?: { paper?: { ref: React.Ref<HTMLDivElement> } };
}

const checkLegacyLogic = (soundLength: number, phraseLength: number) => {
    return soundLength > phraseLength && soundLength % phraseLength === 0;
};

const calculateRatio = (phraseSound: string, phrase: string) => {
    const wordCount = phraseSound.split(' ').length;
    const cleanedPhraseLength = phrase.replace(/[（）()]/g, '').length;
    return Math.floor(wordCount / cleanedPhraseLength);
};

export const CharacterOptionsPicker: React.FC<Props> = ({ anchorEl, onClose, onUpdate, value, slotProps }) => {
    const { t } = useTranslation();

    const open = Boolean(anchorEl);

    const wordPickerRef = useRef<HTMLDivElement | null>(null);
    const wordSelectorRef = useRef<HTMLElement | null>(null);
    const [wordPickerAnchorEl, setWordPickerAnchorEl] = useState<HTMLElement | null>(null);

    const [tempCharacter, setTempCharacter] = useState<SlideComponentHkcsQ3CharacterOptionModel | undefined>(undefined);

    return (
        <>
            <Popover
                data-testid='character-options-picker'
                title={t('slide.legacy.hkcsQ3.relatedCharacters')}
                open={open}
                anchorEl={anchorEl}
                onClose={() => {
                    onClose();
                }}
                anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'center',
                    horizontal: -8,
                }}
                slotProps={{
                    paper: {
                        ref: slotProps?.paper?.ref,
                        sx: {
                            width: SIZE.width,
                            height: SIZE.height,
                            display: 'flex',
                            p: 2,
                        },
                    },
                }}
            >
                <Box
                    sx={(theme) => ({
                        mt: 2,
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignContent: 'center',
                        overflow: 'auto',
                        borderRadius: 1,
                        backgroundColor: (theme.vars || theme).palette.grey[200],
                        ...theme.applyStyles('dark', {
                            backgroundColor: (theme.vars || theme).palette.grey[700],
                        }),
                    })}
                    ref={wordSelectorRef}
                >
                    <Paper sx={{ p: 1, m: 1 }}>
                        <Stack direction='column' gap={1}>
                            <Box
                                sx={(theme) => ({
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'center',
                                    width: '100%',
                                    p: 1,
                                    position: 'relative',
                                    borderRadius: 1,

                                    backgroundColor: theme.palette.grey[100],
                                    ...theme.applyStyles('dark', {
                                        backgroundColor: theme.palette.grey[700],
                                    }),
                                })}
                            >
                                {tempCharacter?.phrase ? (
                                    <>
                                        <Box sx={{ flex: 1, alignContent: 'center' }}>
                                            <PhraseHighlight phrase={tempCharacter?.phrase} highlight={tempCharacter?.phrase} underline={false} />
                                        </Box>
                                        <IconButton
                                            sx={{ position: 'absolute', top: 0, right: 0, m: 0.5 }}
                                            onClick={() => {
                                                setWordPickerAnchorEl(wordSelectorRef.current);
                                            }}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    </>
                                ) : (
                                    <Button
                                        variant='contained'
                                        onClick={() => {
                                            setWordPickerAnchorEl(wordSelectorRef.current);
                                        }}
                                        sx={{
                                            minWidth: '50%',
                                            bottom: 0,
                                            left: 0,
                                            right: 0,
                                            m: 1,
                                        }}
                                    >
                                        {t('slide.legacy.hkcsQ3.selectRelatedCharacter')}
                                    </Button>
                                )}
                            </Box>

                            {tempCharacter?.characterList && tempCharacter?.characterList?.length > 0 && (
                                <ToggleButtonGroup
                                    value={tempCharacter?.isCorrect ? 'true' : 'false'}
                                    exclusive
                                    onChange={(_event, newValue) => {
                                        setTempCharacter((prev) => {
                                            if (!prev || !newValue) {
                                                return prev;
                                            }
                                            return {
                                                ...prev,
                                                isCorrect: newValue === 'true',
                                            };
                                        });
                                    }}
                                    fullWidth
                                    sx={{ mt: 1 }}
                                >
                                    <ToggleButton value='true'>{t('slide.legacy.character.correct')}</ToggleButton>
                                    <ToggleButton value='false'>{t('slide.legacy.character.incorrect')}</ToggleButton>
                                </ToggleButtonGroup>
                            )}

                            {tempCharacter?.phrase && (
                                <>
                                    <Divider textAlign='left'>
                                        <Typography variant='caption'>{t('slide.legacy.character.choosePronunciation')}</Typography>
                                    </Divider>
                                    <Stack direction={'row'} spacing={1} sx={{ mt: 1.5 }}>
                                        {tempCharacter?.phraseSoundGroup && (
                                            <FormControl fullWidth>
                                                <InputLabel id='options-cantonese-pronunciation-select-label'>{t('common.cantonese')}</InputLabel>
                                                <Select
                                                    labelId='options-cantonese-pronunciation-select-label'
                                                    id='options-cantonese-pronunciation-select-id'
                                                    value={tempCharacter?.phraseSoundGroup?.jyutping[0] ? tempCharacter?.phraseSoundGroup.jyutping[0]?.startIndex.toString() : ''}
                                                    label={t('common.cantonese')}
                                                    onChange={(event: SelectChangeEvent) => {
                                                        if (tempCharacter?.phraseSoundGroup) {
                                                            const phraseSoundGroup = {
                                                                ...tempCharacter?.phraseSoundGroup,
                                                                ...(tempCharacter?.phraseSoundGroup?.jyutping && {
                                                                    jyutping: [{ startIndex: parseInt(event.target.value) }],
                                                                }),
                                                            };

                                                            setTempCharacter({
                                                                ...tempCharacter,
                                                                phraseSoundGroup: phraseSoundGroup,
                                                            });
                                                        }
                                                    }}
                                                >
                                                    {tempCharacter?.phraseSound?.jyutping?.map((phraseSound, index) => {
                                                        const isLegacyLogic = checkLegacyLogic(phraseSound.sound?.split(' ').length, tempCharacter?.phrase.length);

                                                        if (isLegacyLogic) {
                                                            return [...Array(calculateRatio(phraseSound.sound, tempCharacter?.phrase))].map((_, idx) => (
                                                                <MenuItem value={idx.toString()} key={idx}>
                                                                    <Stack direction='row' spacing={1} alignItems={'center'}>
                                                                        <Pronunciation
                                                                            checkLegacyLogic={checkLegacyLogic}
                                                                            phraseSound={tempCharacter?.phraseSound}
                                                                            phrase={tempCharacter?.phrase}
                                                                            groupIndex={idx}
                                                                            folder={'jyutping'}
                                                                        />
                                                                        <Typography justifyContent='center' justifyItems={'center'} justifySelf={'center'} align='center'>
                                                                            {t('slide.legacy.character.phraseSoundGroup', {
                                                                                i: idx + 1,
                                                                            })}
                                                                        </Typography>
                                                                    </Stack>
                                                                </MenuItem>
                                                            ));
                                                        } else {
                                                            return (
                                                                <MenuItem value={index.toString()} key={index}>
                                                                    <Stack direction='row' spacing={1} alignItems={'center'}>
                                                                        <Pronunciation
                                                                            checkLegacyLogic={checkLegacyLogic}
                                                                            phraseSound={tempCharacter?.phraseSound}
                                                                            phrase={tempCharacter?.phrase}
                                                                            groupIndex={index}
                                                                            folder={'jyutping'}
                                                                        />
                                                                        <Typography>
                                                                            {t('slide.legacy.character.phraseSoundGroup', {
                                                                                i: index + 1,
                                                                            })}
                                                                        </Typography>
                                                                    </Stack>
                                                                </MenuItem>
                                                            );
                                                        }
                                                    })}
                                                </Select>
                                            </FormControl>
                                        )}

                                        {tempCharacter?.phraseSoundGroup && (
                                            <FormControl fullWidth>
                                                <InputLabel id='options-putonghua-pronunciation-select-label'>{t('common.putonghua')}</InputLabel>
                                                <Select
                                                    labelId='options-putonghua-pronunciation-select-label'
                                                    id='options-putonghua-pronunciation-select-id'
                                                    value={tempCharacter?.phraseSoundGroup?.pinyin && tempCharacter?.phraseSoundGroup.pinyin[0]?.startIndex.toString()}
                                                    label={t('common.putonghua')}
                                                    onChange={(event: SelectChangeEvent) => {
                                                        if (tempCharacter?.phraseSoundGroup) {
                                                            const phraseSoundGroup = {
                                                                ...tempCharacter?.phraseSoundGroup,
                                                                ...(tempCharacter?.phraseSoundGroup?.pinyin && {
                                                                    pinyin: [{ startIndex: parseInt(event.target.value) }],
                                                                }),
                                                            };

                                                            setTempCharacter({
                                                                ...tempCharacter,
                                                                phraseSoundGroup: phraseSoundGroup,
                                                            });
                                                        }
                                                    }}
                                                >
                                                    {tempCharacter?.phraseSound?.pinyin?.map((phraseSound, index) => {
                                                        const isLegacyLogic = checkLegacyLogic(phraseSound.sound?.split(' ').length, tempCharacter?.phrase.length);

                                                        if (isLegacyLogic) {
                                                            return [...Array(calculateRatio(phraseSound.sound, tempCharacter?.phrase))].map((_, idx) => (
                                                                <MenuItem value={idx.toString()} key={idx}>
                                                                    <Stack direction='row' spacing={1} alignItems={'center'}>
                                                                        <Pronunciation
                                                                            checkLegacyLogic={checkLegacyLogic}
                                                                            phraseSound={tempCharacter?.phraseSound}
                                                                            phrase={tempCharacter?.phrase}
                                                                            groupIndex={idx}
                                                                            folder={'pinyin'}
                                                                        />
                                                                        <Typography>
                                                                            {t('slide.legacy.character.phraseSoundGroup', {
                                                                                i: idx + 1,
                                                                            })}
                                                                        </Typography>
                                                                    </Stack>
                                                                </MenuItem>
                                                            ));
                                                        } else {
                                                            return (
                                                                <MenuItem value={index.toString()} key={index}>
                                                                    <Stack direction='row' spacing={1} alignItems={'center'}>
                                                                        <Pronunciation
                                                                            checkLegacyLogic={checkLegacyLogic}
                                                                            phraseSound={tempCharacter?.phraseSound}
                                                                            phrase={tempCharacter?.phrase}
                                                                            groupIndex={index}
                                                                            folder={'pinyin'}
                                                                        />
                                                                        <Typography>
                                                                            {t('slide.legacy.character.phraseSoundGroup', {
                                                                                i: index + 1,
                                                                            })}
                                                                        </Typography>
                                                                    </Stack>
                                                                </MenuItem>
                                                            );
                                                        }
                                                    })}
                                                </Select>
                                            </FormControl>
                                        )}
                                    </Stack>
                                </>
                            )}

                            {tempCharacter && (
                                <>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            justifyContent: 'center',
                                            width: '100%',
                                        }}
                                    >
                                        <Button
                                            variant='contained'
                                            onClick={() => {
                                                onUpdate({
                                                    ...value,
                                                    characterOptions: value.characterOptions ? [...value.characterOptions, tempCharacter] : [tempCharacter],
                                                });
                                                setTempCharacter(undefined);
                                            }}
                                            sx={{
                                                minWidth: '50%',
                                                bottom: 0,
                                                left: 0,
                                                right: 0,
                                                m: 1,
                                            }}
                                        >
                                            {t('slide.legacy.hkcsQ3.addRelatedCharacter')}
                                        </Button>
                                    </Box>
                                </>
                            )}
                        </Stack>
                    </Paper>

                    <Paper sx={{ p: 1, m: 1, height: '100%' }}>
                        <Divider textAlign='left'>
                            <Typography variant='caption'>{t('slide.legacy.hkcsQ3.relatedCharacters')}</Typography>
                        </Divider>
                        <Grid container spacing={1} sx={{ justifyContent: 'flex-start', mt: 1.5 }}>
                            {value.characterOptions?.map((data, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        position: 'relative',
                                        display: 'inline-flex',
                                        flexShrink: 0,
                                    }}
                                >
                                    <Button
                                        value={data.characterList[data.character]?.character || ''}
                                        sx={{
                                            flexShrink: 0,
                                            minWidth: 'max-content',
                                            pl: 2,
                                            pr: 4,
                                            color: 'white',

                                            backgroundColor: data.isCorrect ? '#2E7D32' : '#D32F2F',

                                            '&:hover': {
                                                backgroundColor: data.isCorrect ? '#2E7D32' : '#D32F2F',
                                                cursor: 'default',
                                            },

                                            '&.Mui-selected:hover': {
                                                backgroundColor: data.isCorrect ? '#2E7D32' : '#D32F2F',
                                            },
                                        }}
                                    >
                                        {data.characterList[data.character]?.character}
                                    </Button>

                                    <IconButton
                                        component='span'
                                        size='small'
                                        onClick={() => {
                                            onUpdate({
                                                ...value,
                                                characterOptions: value.characterOptions?.filter((_, i) => i !== index),
                                            });
                                        }}
                                        sx={{
                                            position: 'absolute',
                                            right: 4,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            color: 'inherit',
                                            zIndex: 1,
                                        }}
                                    >
                                        <CancelIcon fontSize='small' />
                                    </IconButton>
                                </Box>
                            ))}
                        </Grid>
                    </Paper>
                </Box>
            </Popover>

            {wordPickerAnchorEl && (
                <WordPicker
                    type='word'
                    title={t('slide.legacy.character.characterSearch')}
                    anchorEl={wordPickerAnchorEl}
                    slotProps={{
                        paper: {
                            ref: wordPickerRef,
                        },
                    }}
                    value={{
                        phrase: tempCharacter?.phrase || '',
                    }}
                    onClose={() => {
                        setWordPickerAnchorEl(null);
                    }}
                    onUpdate={(settings) => {
                        if (settings.characterList && settings.characterList.length > 0) {
                            setTempCharacter({
                                characterList: settings.characterList,
                                character: 0,

                                characterSound: settings.characterSound || {
                                    jyutping: [],
                                    pinyin: [],
                                },
                                phrase: settings.phrase,
                                phraseSound: settings.phraseSound || {
                                    jyutping: [],
                                    pinyin: [],
                                },
                                phraseSoundGroup: settings.phraseSoundGroup || {
                                    jyutping: [{ startIndex: 0 }],
                                    pinyin: [{ startIndex: 0 }],
                                },
                                isCorrect: true,
                            });
                        }
                    }}
                />
            )}
        </>
    );
};
